import React from 'react';
import { 
  MessageSquare, 
  Lightbulb, 
  PenTool, 
  Code2, 
  TestTube, 
  Rocket,
  ArrowRight
} from 'lucide-react';

const Process = () => {
  const steps = [
    {
      icon: MessageSquare,
      title: 'Discovery & Consultation',
      description: 'We begin by understanding your vision, goals, and requirements through detailed consultations.',
      activities: [
        'Requirements gathering',
        'Stakeholder interviews',
        'Market analysis',
        'Technical feasibility study'
      ]
    },
    {
      icon: Lightbulb,
      title: 'Strategy & Planning',
      description: 'Our team develops a comprehensive roadmap and technical architecture for your project.',
      activities: [
        'Project roadmap creation',
        'Technology stack selection',
        'Timeline & milestone planning',
        'Resource allocation'
      ]
    },
    {
      icon: PenTool,
      title: 'Design & Prototyping',
      description: 'We create intuitive designs and interactive prototypes to visualize the final product.',
      activities: [
        'UI/UX design',
        'Wireframing',
        'Interactive prototypes',
        'Design system creation'
      ]
    },
    {
      icon: Code2,
      title: 'Development',
      description: 'Our developers bring your application to life using agile methodologies and best practices.',
      activities: [
        'Sprint planning',
        'Code development',
        'API integration',
        'Database design'
      ]
    },
    {
      icon: TestTube,
      title: 'Testing & Quality Assurance',
      description: 'Rigorous testing ensures your application is bug-free, secure, and performs optimally.',
      activities: [
        'Unit testing',
        'Integration testing',
        'Performance testing',
        'Security testing'
      ]
    },
    {
      icon: Rocket,
      title: 'Deployment & Support',
      description: 'We handle the launch and provide ongoing support to ensure continued success.',
      activities: [
        'Deployment setup',
        'Production launch',
        'Monitoring & maintenance',
        'Continuous improvements'
      ]
    }
  ];

  return (
    <section className="py-20 bg-dark-surface/30">
      <div className="max-w-7xl mx-auto content-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Our Development <span className="brand-gradient">Process</span>
          </h2>
          <p className="text-lg text-dark-muted max-w-2xl mx-auto">
            A proven methodology that ensures successful project delivery from concept to deployment.
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-brand via-brand-light to-highlight"></div>

          {/* Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  {/* Step Card */}
                  <div className="bg-dark-bg border border-dark-border rounded-xl p-6 hover:border-brand/50 transition-all duration-300 h-full">
                    {/* Step Number */}
                    <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br from-brand to-brand-light rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>

                    {/* Icon */}
                    <div className="w-12 h-12 bg-brand/10 rounded-lg flex items-center justify-center mb-4 mx-auto lg:mx-0">
                      <Icon className="w-6 h-6 text-brand" />
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold mb-2 text-center lg:text-left">
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p className="text-dark-muted text-sm mb-4 text-center lg:text-left">
                      {step.description}
                    </p>

                    {/* Activities */}
                    <div className="space-y-1">
                      {step.activities.map((activity, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <ArrowRight className="w-3 h-3 text-brand/60 flex-shrink-0" />
                          <span className="text-xs text-dark-muted">{activity}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Connection Arrow (for mobile) */}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden flex justify-center py-4">
                      <ArrowRight className="w-6 h-6 text-brand rotate-90" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-dark-muted mb-4">
            Ready to start your project? Let's discuss how we can help bring your vision to life.
          </p>
          <button 
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="action-btn"
          >
            Start Your Project
          </button>
        </div>
      </div>
    </section>
  );
};

export default Process;

